/**
 * Hook for media conversion.
 *
 * Two execution paths, selected by the current API mode:
 *
 * LOCAL (desktop / local dev — FFmpeg on the user's machine, free, no upload):
 *   1. POST multipart /api/v1/convert/audio|video -> {job_id}
 *   2. Poll /api/v1/jobs/{job_id} until completed
 *   3. Result file lives on disk; download via /api/v1/convert/download/{job_id}
 *
 * CLOUD (serverless — used when the local machine cannot run conversions):
 *   1. POST /api/v1/convert/request-upload -> {upload_url, s3_key, job_id}
 *   2. PUT upload_url (direct S3 upload with progress)
 *   3. POST /api/v1/convert/start -> {job_id, status}
 *   4. Poll /api/v1/jobs/{job_id} until completed
 *   5. Download from presigned result_url
 */

import { useState, useCallback, useRef } from 'react';
import { getApiBaseUrl } from '../services/urlResolver';
import { firebaseClient } from '../services/firebase';
import { isLocalMode } from '../utils/apiMode';

import { handleError } from '../utils/errorHandler';
import { JSON_HEADERS, extractResponseErrorMessage, unwrapEnvelope } from '../utils/httpUtils';
import { getFreshResultUrl } from '../services/jobs';
import { URLService } from '../services/url.service';

// ============================================================================
// Types
// ============================================================================

export interface ConversionSettings {
  outputFormat: string;
  quality: 'low' | 'medium' | 'high' | 'very_high' | 'lossless';
  preserveMetadata: boolean;
  normalizeAudio: boolean;
}

export interface ConversionResult {
  success: boolean;
  jobId: string;
  outputFileName: string;
  outputFormat: string;
  outputSize: number;
  duration?: number;
  /**
   * The link the job reported when it finished. On Cloud Run it is presigned
   * and dies an hour later: a download resolves a fresh one with
   * getConversionDownloadUrl(jobId) instead of using this.
   */
  downloadUrl: string;
  /** Local filesystem path of the converted file (desktop "Show in Folder"). */
  outputPath?: string;
}

interface JobStatusResponse {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'error';
  progress: number;
  message?: string;
  result_url?: string;
  output_size?: number;
  output_format?: string;
  duration?: number;
  error?: string;
  extra_metadata?: {
    output_path?: string;
    output_size_bytes?: number;
    input_duration_seconds?: number;
  };
}

interface RequestUploadResponse {
  upload_url: string;
  s3_key: string;
  job_id: string;
  content_type: string;
}

interface SubmitResponse {
  job_id: string;
  status: string;
}

// ============================================================================
// Progress Message Mapping
// ============================================================================

/**
 * Maps technical backend messages to user-friendly display messages.
 * Backend messages expose internal details (S3, GCS, etc.) that should
 * not be shown to end users.
 */
const FRIENDLY_MESSAGES: Record<string, string> = {
  'Downloading source file from S3...': 'Preparing file for conversion...',
  'Converting media file...': 'Converting...',
  'Uploading converted file to S3...': 'Saving converted file...',
  'Conversion complete!': 'Complete!',
  'Starting media conversion...': 'Starting conversion...',
  'Job is queued for processing': 'Waiting for processing...',
  'Job is being processed': 'Processing...',
  'Awaiting progress update from RunPod...': 'Processing...',
};

function getFriendlyMessage(message: string): string {
  return FRIENDLY_MESSAGES[message] ?? message;
}

/**
 * Authorization header, present only when a token is available. Desktop/local
 * resolves to local_dev_user and does not require a Firebase token, so a bare
 * `Bearer null` must never be sent.
 */
function bearer(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ============================================================================
// Hook
// ============================================================================

export function useMediaConversion() {
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const maxProgressRef = useRef(0);

  const convert = useCallback(
    async (file: File, settings: ConversionSettings): Promise<ConversionResult> => {
      setIsConverting(true);
      setProgress(0);
      maxProgressRef.current = 0;
      setStatusMessage('Preparing...');
      setError(null);
      setResult(null);

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const mediaType: 'audio' | 'video' = file.type.startsWith('video/') ? 'video' : 'audio';
        const baseUrl = getApiBaseUrl();
        const local = isLocalMode();

        // The cloud path uploads to S3 + runs on RunPod GPU and therefore needs
        // a Firebase token. The local path runs FFmpeg on the user's machine
        // (free) and resolves to local_dev_user, so a token is optional there.
        const authToken = await firebaseClient.getIdToken();
        if (!authToken && !local) {
          throw new Error('Authentication required. Please sign in to convert files.');
        }

        setProgress(5);

        // Steps 1-3: submit the job and obtain its id.
        const jobId = local
          ? await submitLocalConversion(
              baseUrl,
              authToken,
              file,
              settings,
              mediaType,
              signal,
              setProgress,
              setStatusMessage
            )
          : await submitCloudConversion(
              baseUrl,
              authToken,
              file,
              settings,
              mediaType,
              signal,
              setProgress,
              setStatusMessage
            );

        setProgress(45);
        maxProgressRef.current = 45;
        setStatusMessage('Converting...');

        // Step 4: poll for completion.
        await pollConversionProgress(
          jobId,
          baseUrl,
          authToken,
          setProgress,
          setStatusMessage,
          maxProgressRef
        );

        // Step 5: read the final result.
        const resultResponse = await fetch(`${baseUrl}/api/v1/jobs/${jobId}`, {
          headers: bearer(authToken),
          signal,
        });

        if (!resultResponse.ok) {
          throw new Error('Failed to get conversion result');
        }

        const resultData = unwrapEnvelope<JobStatusResponse>(await resultResponse.json());

        if (resultData.status === 'failed' || resultData.status === 'error') {
          throw new Error(resultData.error || 'Conversion failed');
        }

        const meta = resultData.extra_metadata ?? {};
        const downloadUrl = URLService.getFullUrl(resultData.result_url ?? '');

        const finalResult: ConversionResult = {
          success: true,
          jobId,
          outputFileName: file.name.replace(/\.[^.]+$/, settings.outputFormat),
          outputFormat: settings.outputFormat,
          outputSize: resultData.output_size ?? meta.output_size_bytes ?? 0,
          duration: resultData.duration ?? meta.input_duration_seconds,
          downloadUrl,
          outputPath: meta.output_path,
        };

        setResult(finalResult);
        setProgress(100);
        setStatusMessage('Complete!');
        return finalResult;
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          setError('Conversion cancelled');
        } else {
          const errorMessage = handleError(err, 'MediaConversion', 'Conversion failed');
          setError(errorMessage);
        }
        throw err;
      } finally {
        setIsConverting(false);
        abortControllerRef.current = null;
      }
    },
    []
  );

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const reset = useCallback(() => {
    setProgress(0);
    setStatusMessage('');
    setResult(null);
    setError(null);
    setIsConverting(false);
  }, []);

  return {
    convert,
    cancel,
    reset,
    progress,
    statusMessage,
    result,
    error,
    isConverting,
  };
}

// ============================================================================
// Local (FFmpeg) submission
// ============================================================================

/**
 * Submit a file to the local FFmpeg conversion endpoint via multipart upload.
 * Returns the job id once the server has accepted the file.
 */
async function submitLocalConversion(
  baseUrl: string,
  authToken: string | null,
  file: File,
  settings: ConversionSettings,
  mediaType: 'audio' | 'video',
  signal: AbortSignal,
  setProgress: (p: number) => void,
  setStatusMessage: (m: string) => void
): Promise<string> {
  setStatusMessage('Uploading file...');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('output_format', settings.outputFormat);
  formData.append('quality', settings.quality);
  formData.append('preserve_metadata', String(settings.preserveMetadata));
  if (mediaType === 'audio') {
    formData.append('normalize_audio', String(settings.normalizeAudio));
  }

  const endpoint = mediaType === 'video' ? '/api/v1/convert/video' : '/api/v1/convert/audio';

  const data = await uploadMultipartWithProgress(
    `${baseUrl}${endpoint}`,
    formData,
    authToken,
    signal,
    // Scale upload progress from 5-40%.
    (uploadProgress) => setProgress(5 + Math.round(uploadProgress * 0.35))
  );

  if (!data.job_id) {
    throw new Error('Conversion server did not return a job ID');
  }
  return data.job_id;
}

/**
 * POST a multipart FormData payload with upload-progress events.
 *
 * Uses XHR (fetch lacks upload progress). Sets the CSRF header required by the
 * backend; the browser sets the multipart Content-Type/boundary automatically.
 */
function uploadMultipartWithProgress(
  url: string,
  formData: FormData,
  authToken: string | null,
  signal: AbortSignal,
  onProgress: (progress: number) => void
): Promise<SubmitResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded / event.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(unwrapEnvelope<SubmitResponse>(JSON.parse(xhr.responseText)));
        } catch {
          reject(new Error('Invalid response from conversion server'));
        }
        return;
      }
      let message = `Conversion request failed: ${xhr.statusText || xhr.status}`;
      try {
        const parsed = JSON.parse(xhr.responseText);
        message = extractResponseErrorMessage(parsed) || message;
      } catch {
        // Non-JSON error body — keep the default message.
      }
      reject(new Error(message));
    });

    xhr.addEventListener('error', () =>
      reject(new Error('Conversion request failed: network error'))
    );
    xhr.addEventListener('abort', () => {
      const abortError = new Error('Conversion cancelled');
      abortError.name = 'AbortError';
      reject(abortError);
    });

    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    signal.addEventListener('abort', () => xhr.abort(), { once: true });

    xhr.open('POST', url);
    // CSRF protection: raw XHR uploads must carry X-Requested-With.
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    if (authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    }
    xhr.send(formData);
  });
}

// ============================================================================
// Cloud (S3 + RunPod) submission
// ============================================================================

/**
 * Run the cloud submission steps (request presigned URL, upload to S3, start the
 * RunPod job) and return the job id. A Firebase token is required here.
 */
async function submitCloudConversion(
  baseUrl: string,
  authToken: string | null,
  file: File,
  settings: ConversionSettings,
  mediaType: 'audio' | 'video',
  signal: AbortSignal,
  setProgress: (p: number) => void,
  setStatusMessage: (m: string) => void
): Promise<string> {
  if (!authToken) {
    throw new Error('Authentication required. Please sign in to convert files.');
  }

  setStatusMessage('Requesting upload URL...');
  const uploadInfo = await requestUploadUrl(baseUrl, authToken, file, mediaType, signal);

  setProgress(5);
  setStatusMessage('Uploading file...');
  // Scale upload progress from 5-40%.
  await uploadToS3(uploadInfo.upload_url, file, uploadInfo.content_type, (uploadProgress) => {
    setProgress(5 + Math.round(uploadProgress * 0.35));
  });

  setProgress(40);
  setStatusMessage('Starting conversion...');
  await startConversion(
    baseUrl,
    authToken,
    uploadInfo.job_id,
    uploadInfo.s3_key,
    settings.outputFormat,
    settings.quality,
    mediaType,
    signal
  );

  return uploadInfo.job_id;
}

async function requestUploadUrl(
  baseUrl: string,
  authToken: string,
  file: File,
  mediaType: string,
  signal: AbortSignal
): Promise<RequestUploadResponse> {
  const response = await fetch(`${baseUrl}/api/v1/convert/request-upload`, {
    method: 'POST',
    headers: {
      ...JSON_HEADERS,
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      filename: file.name,
      media_type: mediaType,
      file_size: file.size,
    }),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      extractResponseErrorMessage(errorData) || `Upload request failed: ${response.statusText}`
    );
  }

  const data = await response.json();
  return unwrapEnvelope<RequestUploadResponse>(data);
}

async function uploadToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded / event.total);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('S3 upload failed: network error')));
    xhr.addEventListener('abort', () => {
      const abortError = new Error('Upload cancelled');
      abortError.name = 'AbortError';
      reject(abortError);
    });

    xhr.open('PUT', uploadUrl);
    // Must use the exact Content-Type that was used to sign the presigned URL
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
}

async function startConversion(
  baseUrl: string,
  authToken: string,
  jobId: string,
  s3Key: string,
  outputFormat: string,
  quality: string,
  mediaType: string,
  signal: AbortSignal
): Promise<void> {
  const response = await fetch(`${baseUrl}/api/v1/convert/start`, {
    method: 'POST',
    headers: {
      ...JSON_HEADERS,
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      job_id: jobId,
      s3_key: s3Key,
      output_format: outputFormat,
      quality,
      media_type: mediaType,
    }),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      extractResponseErrorMessage(errorData) || `Failed to start conversion: ${response.statusText}`
    );
  }
}

// ============================================================================
// Download link
// ============================================================================

/**
 * The converted file's download link, as the backend serves it now.
 *
 * On Cloud Run it is a presigned S3 URL that dies an hour after it was signed,
 * and the backend re-signs it on every read of the job; a local conversion's
 * link is a relative path on this backend (made absolute, so the fetch hits the
 * backend and not the renderer origin). A download calls this right before it
 * starts instead of reusing the link that came with the result.
 */
export async function getConversionDownloadUrl(jobId: string): Promise<string> {
  return URLService.getFullUrl(await getFreshResultUrl(jobId));
}

// ============================================================================
// Shared polling
// ============================================================================

async function pollConversionProgress(
  jobId: string,
  baseUrl: string,
  authToken: string | null,
  setProgress: (p: number) => void,
  setStatusMessage: (m: string) => void,
  maxProgressRef: { current: number },
  maxAttempts = 120,
  intervalMs = 2000
): Promise<void> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await fetch(`${baseUrl}/api/v1/jobs/${jobId}`, {
      headers: bearer(authToken),
    });

    if (!response.ok) {
      throw new Error('Failed to check conversion status');
    }

    const responseData = await response.json();
    // Unwrap the {success, data} response envelope from the jobs endpoint
    const data: JobStatusResponse = unwrapEnvelope<JobStatusResponse>(responseData);

    // Scale polling progress from 45-95%
    const displayProgress = Math.min(95, Math.max(45, 45 + (data.progress || 0) * 0.5));

    // Enforce monotonic progress - never allow the bar to go backwards.
    // Backend GCS reads can temporarily return stale/empty data, causing
    // progress to drop to 0 and frontend to show 45%.
    if (displayProgress > maxProgressRef.current) {
      maxProgressRef.current = displayProgress;
      setProgress(displayProgress);
    }

    if (data.message) {
      setStatusMessage(getFriendlyMessage(data.message));
    }

    if (data.status === 'completed') {
      return;
    }

    if (data.status === 'failed' || data.status === 'error') {
      throw new Error(data.error || 'Conversion failed');
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('Conversion timed out');
}

export default useMediaConversion;
